import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './qrCourse.scss';
import { 
  generateMailOtp, 
  verifyMailOtp, 
  resendMailOtp, 
  QRcodeChecksum,
  getQrCourseDetails 
} from 'services/qrCourses';

const QrCourse = () => {
  const { courseId } = useParams();
  const courseIdFromUrl = courseId;

  // Course Details State
  const [courseDetails, setCourseDetails] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isLoadingCourse, setIsLoadingCourse] = useState(true);
  const [courseError, setCourseError] = useState('');

  // Step Management
  const [currentStep, setCurrentStep] = useState(1);
  const [isStepTransitioning, setIsStepTransitioning] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    mobile: ''
  });

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checksumError, setChecksumError] = useState('');

  const inputRefs = useRef([]);
  const containerRef = useRef(null);

  // Fetch course details on mount
  useEffect(() => {
    if (courseIdFromUrl) {
      fetchCourseDetails();
    } else {
      setCourseError('No course ID found in URL');
      setIsLoadingCourse(false);
    }
  }, [courseIdFromUrl]);

  const fetchCourseDetails = async () => {
    setIsLoadingCourse(true);
    setCourseError('');
    try {
      const response = await getQrCourseDetails({ courseId: courseIdFromUrl });
      if (response?.code === 200 && response?.data?.courseDetails) {
        const details = response.data.courseDetails;
        setCourseDetails(details);
        
        if (details.CourseVariants && details.CourseVariants.length > 0) {
          setSelectedVariant(details.CourseVariants[0]);
        }
      } else {
        setCourseError(response?.message || 'Failed to fetch course details');
      }
    } catch (err) {
      setCourseError(err?.message || 'Something went wrong');
    } finally {
      setIsLoadingCourse(false);
    }
  };

  // Timer for OTP
  useEffect(() => {
    let interval = null;
    if (isOtpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isOtpSent, timer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile' && value && (!/^\d+$/.test(value) || value.length > 10)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const generateMaskedEmail = (emailStr) => {
    if (!emailStr || !emailStr.includes('@')) return '';
    const [localPart, domainPart] = emailStr.split('@');
    if (localPart.length <= 2) return `${localPart.slice(0, 1)}*@${domainPart}`;
    const firstChar = localPart.charAt(0);
    const lastChar = localPart.charAt(localPart.length - 1);
    const maskStars = '*'.repeat(localPart.length - 2);
    return `${firstChar}${maskStars}${lastChar}@${domainPart}`;
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    const pasteArray = pasteData.slice(0, 6).split('').filter(char => /^\d$/.test(char));
    if (pasteArray.length > 0) {
      const newOtp = [...otp];
      pasteArray.forEach((char, idx) => { if (idx < 6) newOtp[idx] = char; });
      setOtp(newOtp);
      const lastFilledIndex = Math.min(pasteArray.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
    }
  };

  // Step Navigation with Animation
  const goToStep = (step) => {
    if (isStepTransitioning) return;
    setIsStepTransitioning(true);
    
    // Add exit animation
    const container = containerRef.current;
    if (container) {
      container.classList.add('step-exit');
    }
    
    setTimeout(() => {
      setCurrentStep(step);
      setError('');
      setSuccess('');
      if (container) {
        container.classList.remove('step-exit');
      }
      setIsStepTransitioning(false);
    }, 300);
  };

  // Step 1: Continue to Step 2
  const handleContinueToForm = () => {
    goToStep(2);
  };

  // Step 2: Send OTP
  const handleSendOtp = async () => {
    const { firstname, lastname, email, mobile } = formData;
    if (!firstname.trim() || !lastname.trim() || !email.trim() || !mobile.trim()) {
      setError('Please fill in all fields');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setError('Please enter a valid email address'); return; }
    if (mobile.length !== 10) { setError('Please enter a valid 10-digit mobile number'); return; }

    setIsSending(true);
    setError('');
    setSuccess('');

    try {
      const response = await generateMailOtp({ firstname, lastname, email, mobile });
      if (response?.code === 200 || response?.success) {
        setIsOtpSent(true);
        setTimer(60);
        setCanResend(false);
        setSuccess(response?.message || 'OTP sent successfully!');
        setOtp(['', '', '', '', '', '']);
        goToStep(3);
        setTimeout(() => { inputRefs.current[0]?.focus(); }, 400);
      } else {
        setError(response?.message || 'Failed to send OTP');
      }
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setIsSending(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    setIsResending(true);
    setError('');
    setSuccess('');

    try {
      const response = await resendMailOtp({
        firstname: formData.firstname,
        lastname: formData.lastname,
        email: formData.email,
        mobile: formData.mobile
      });
      if (response?.code === 200 || response?.success) {
        setTimer(60);
        setCanResend(false);
        setSuccess(response?.message || 'OTP resent successfully!');
        setOtp(['', '', '', '', '', '']);
      } else {
        setError(response?.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError(err?.message || 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');
    if (otpValue.length !== 6) { setError('Please enter all 6 digits'); return; }

    setIsVerifying(true);
    setError('');
    setSuccess('');
    setChecksumError('');

    try {
      const response = await verifyMailOtp({ email: formData.email, otp: otpValue });

      if (response?.code === 200 || response?.success) {
        const accessToken = response?.data?.access_token;
        setIsVerified(true);
        setIsCheckingOut(true);
        setChecksumError('');

        try {
          const checksumPayload = {
            ammount: getCourseFee(),
            courseID: courseIdFromUrl,
            platformType: "chrome",
            access_token: accessToken,
          };

          if (selectedVariant) {
            checksumPayload.variantcode = selectedVariant.variantcode;
          }

          const checksumResponse = await QRcodeChecksum(checksumPayload);

          if (checksumResponse?.code === 200 || checksumResponse?.success) {
            const pgUrl = checksumResponse?.data?.pgurl;
            if (pgUrl) {
              window.location.href = pgUrl;
            } else {
              setChecksumError('Payment URL not found. Please try again.');
              setIsCheckingOut(false);
            }
          } else {
            setChecksumError(checksumResponse?.message || 'Something went wrong. Please try again.');
            setIsCheckingOut(false);
          }

        } catch (checksumErr) {
          setChecksumError(
            checksumErr?.response?.data?.message ||
            checksumErr?.message ||
            'Payment initiation failed. Please try again.'
          );
          setIsCheckingOut(false);
        }

      } else {
        setError(response?.message || 'Invalid OTP. Please try again.');
        inputRefs.current.forEach(ref => {
          if (ref) {
            ref.classList.add('shake-verify');
            setTimeout(() => ref.classList.remove('shake-verify'), 500);
          }
        });
        setIsVerifying(false);
      }
    } catch (err) {
      setError(err?.message || 'Invalid OTP. Please try again.');
      inputRefs.current.forEach(ref => {
        if (ref) {
          ref.classList.add('shake-verify');
          setTimeout(() => ref.classList.remove('shake-verify'), 500);
        }
      });
      setIsVerifying(false);
    }
  };

  const getCourseFee = () => {
    if (!courseDetails) return '0';
    
    if (courseDetails.CourseVariants && courseDetails.CourseVariants.length > 0) {
      if (selectedVariant) {
        return selectedVariant.newstdprice || selectedVariant.newstdprice || '0';
      }
      return courseDetails.CourseVariants[0]?.newstdprice || '0';
    }
    
    return courseDetails.SRNewStudentCourseFee || courseDetails.CourseFee || '0';
  };

  const getDisplayFee = () => {
    const fee = getCourseFee();
    return parseInt(fee).toLocaleString('en-IN');
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading State
  if (isLoadingCourse) {
    return (
      <div className="page-wrapper-verify">
        <div className="ambient-glows">
          <div className="glow-sphere sphere-primary"></div>
          <div className="glow-sphere sphere-secondary"></div>
        </div>
        <div className="container-verify">
          <div className="flow-step-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner-large-verify" style={{ margin: '0 auto 20px' }}></div>
            <h2 style={{ color: '#0f172a', fontSize: '20px' }}>Loading course details...</h2>
            <p style={{ color: '#64748b', marginTop: '8px' }}>Please wait while we fetch the course information</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (courseError) {
    return (
      <div className="page-wrapper-verify">
        <div className="ambient-glows">
          <div className="glow-sphere sphere-primary"></div>
          <div className="glow-sphere sphere-secondary"></div>
        </div>
        <div className="container-verify">
          <div className="flow-step-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ color: '#b91c1c', fontSize: '20px' }}>Something went wrong</h2>
            <p style={{ color: '#64748b', marginTop: '8px' }}>{courseError}</p>
          </div>
        </div>
      </div>
    );
  }

  // No course found
  if (!courseDetails) {
    return (
      <div className="page-wrapper-verify">
        <div className="ambient-glows">
          <div className="glow-sphere sphere-primary"></div>
          <div className="glow-sphere sphere-secondary"></div>
        </div>
        <div className="container-verify">
          <div className="flow-step-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
            <h2 style={{ color: '#0f172a', fontSize: '20px' }}>Course not found</h2>
            <p style={{ color: '#64748b', marginTop: '8px' }}>The course you're looking for doesn't exist</p>
          </div>
        </div>
      </div>
    );
  }

  // Main Render
  return (
    <div className="page-wrapper-verify">
      <div className="ambient-glows">
        <div className="glow-sphere sphere-primary"></div>
        <div className="glow-sphere sphere-secondary"></div>
      </div>

      <div className="container-verify" ref={containerRef}>

        {/* VERIFIED STATE - Payment Processing */}
        {isVerified ? (
          <div className="flow-step-container">
            <div className="flow-header">
              <span className="badge">✓ Verified</span>
              <h1>Welcome, {formData.firstname}!</h1>
              <p>
                {isCheckingOut
                  ? 'Processing your enrollment, please wait...'
                  : checksumError
                    ? 'Verification successful, but enrollment failed.'
                    : 'Your email has been verified successfully.'}
              </p>
            </div>

            {isCheckingOut && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                <span
                  className="spinner-verify"
                  style={{
                    width: 36,
                    height: 36,
                    borderWidth: 3,
                    borderColor: 'rgba(50,104,176,0.2)',
                    borderTopColor: '#3268B0'
                  }}
                />
              </div>
            )}

            {!isCheckingOut && checksumError && (
              <div className="msg-error-verify" style={{ marginTop: 20 }}>
                <span className="msg-icon-verify">✕</span>
                {checksumError}
              </div>
            )}
          </div>

        ) : (
          <>
            {/* ========== STEP 1: Course Details + Variants ========== */}
            {currentStep === 1 && (
              <div className="flow-step-container">
                <div className="flow-header">
                  <span className="badge">
                    <span className="badge-dot"></span> Step 01
                  </span>
                  <h1>{courseDetails.CourseTitle || 'Course Enrollment'}</h1>
                  <p>Select your plan and continue</p>
                </div>

                <div className="course-payment-pane">
                  <div className="course-meta-details">
                    {/* Variants Selection */}
                    {courseDetails.CourseVariants && courseDetails.CourseVariants.length > 0 ? (
                      <div className="variant-selector">
                        <label className="variant-label">Select Plan</label>
                        <div className="variant-options">
                          {courseDetails.CourseVariants.map((variant) => (
                            <div
                              key={variant.id}
                              className={`variant-card ${selectedVariant?.id === variant.id ? 'active' : ''}`}
                              onClick={() => setSelectedVariant(variant)}
                            >
                              <div className="variant-name">{variant.variantname}</div>
                              <div className="variant-price">
                                ₹{parseInt(variant.newstdprice || variant.newstdprice || '0').toLocaleString('en-IN')}
                              </div>
                              {variant.description && (
                                <div className="variant-desc">{variant.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="no-variant-fee">
                        <div className="fee-amount">
                          ₹{getDisplayFee()}
                        </div>
                        {courseDetails.CourseDuration && (
                          <div className="duration-info">
                            <span className="duration-icon">📅</span> Duration: {courseDetails.CourseDuration}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Invoice Summary */}
                  <div className="invoice-summary">
                    <div className="price-label">Course Fee</div>
                    <div className="price-value">₹{getDisplayFee()}</div>
                    {courseDetails.CourseVariants && courseDetails.CourseVariants.length > 0 && selectedVariant && (
                      <div className="variant-badge">
                        {selectedVariant.variantname}
                      </div>
                    )}
                    <div className="secure-badge">
                      <span>🔒</span> Secure Payment
                    </div>
                  </div>
                </div>

                <button
                  className="action-trigger-btn btn-checkout"
                  onClick={handleContinueToForm}
                >
                  Continue <span className="btn-arrow">→</span>
                </button>
              </div>
            )}

            {/* ========== STEP 2: User Details Form ========== */}
            {currentStep === 2 && (
              <div className="flow-step-container">
                <div className="flow-header">
                  <span className="badge badge-alt">
                    <span className="badge-dot"></span> Step 02
                  </span>
                  <h1>Enter Your Details</h1>
                  <p>Fill in your information to proceed</p>
                </div>

                <div className="form-section-verify">
                  <div className="form-row-grid">
                    <div className="form-group-verify">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="firstname"
                        placeholder="Enter first name"
                        value={formData.firstname}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-verify">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="lastname"
                        placeholder="Enter last name"
                        value={formData.lastname}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row-grid">
                    <div className="form-group-verify">
                      <label>Email Address</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group-verify">
                      <label>Mobile Number</label>
                      <input
                        type="tel"
                        name="mobile"
                        placeholder="Enter 10-digit number"
                        value={formData.mobile}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="step-navigation">
                    <button
                      className="btn-navigation-back"
                      onClick={() => goToStep(1)}
                    >
                      ← Back
                    </button>
                    <button
                      className="action-trigger-btn btn-primary-flow"
                      onClick={handleSendOtp}
                      disabled={isSending}
                    >
                      {isSending ? (
                        <span className="spinner-verify"></span>
                      ) : (
                        'Send Email OTP →'
                      )}
                    </button>
                  </div>

                  {error && (
                    <div className="msg-error-verify">
                      <span className="msg-icon-verify">✕</span>{error}
                    </div>
                  )}
                  {success && (
                    <div className="msg-success-verify">
                      <span className="msg-icon-verify">✓</span>{success}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========== STEP 3: OTP Verification ========== */}
            {currentStep === 3 && (
              <div className="flow-step-container">
                <div className="flow-header">
                  <span className="badge badge-alt">
                    <span className="badge-dot"></span> Step 03
                  </span>
                  <h1>Verify OTP</h1>
                  <p>OTP sent to <strong>{generateMaskedEmail(formData.email)}</strong></p>
                </div>

                <div className="otp-section-verify">
                  <div className="otp-inputs-verify">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        className="otp-input-verify"
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={index === 0 ? handleOtpPaste : undefined}
                        disabled={isVerifying}
                        placeholder="0"
                      />
                    ))}
                  </div>

                  <div className="otp-actions-verify">
                    <button
                      type="button"
                      className="btn-resend-verify"
                      onClick={handleResendOtp}
                      disabled={!canResend || isResending}
                    >
                      {isResending ? (
                        <><span className="spinner-small-verify"></span>Resending...</>
                      ) : (
                        canResend ? 'Resend OTP' : `Resend in ${formatTimer(timer)}`
                      )}
                    </button>
                  </div>

                  <div className="step-navigation">
                    <button
                      className="btn-navigation-back"
                      onClick={() => goToStep(2)}
                    >
                      ← Back
                    </button>
                    <button
                      className="action-trigger-btn btn-secondary-flow"
                      onClick={handleVerifyOtp}
                      disabled={isVerifying || otp.join('').length !== 6}
                    >
                      {isVerifying ? <span className="spinner-verify"></span> : 'Verify OTP'}
                    </button>
                  </div>

                  {error && (
                    <div className="msg-error-verify">
                      <span className="msg-icon-verify">✕</span>{error}
                    </div>
                  )}
                  {success && (
                    <div className="msg-success-verify">
                      <span className="msg-icon-verify">✓</span>{success}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default QrCourse;